// scheduleSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "./store";
import http from "app/requests/axiosInstance";

export enum AvailabilityType {
  UNSCHEDULED = "unscheduled",
  SCHEDULED = "scheduled",
}

export interface Schedule {
  employeeId: number;
  employeeName: string;
  employeeUid: string;
  date: string;
  appointments: any[];
  blockedTimes: any[];
  availability: {
    id: number;
    type: string;
    dayOrDate: string;
    timeStart: string;
    timeEnd: string;
    status: string;
    availabilityableType: string;
    availabilityableId: number;
    startOfWeek: string;
  };
}

export interface SchedulesSliceState {
  [date: string]: {
    [employeeId: number]: Schedule;
  };
}

const initialState: SchedulesSliceState = {};

// Thunk to fetch schedules for multiple employees and dates
export const getSchedules = createAsyncThunk(
  "schedules/getSchedulesStatus",
  async (
    {
      employeeIds = [],
      date,
      nameKey,
    }: {
      employeeIds: number[];
      date: Date;
      nameKey: string;
    },
    thunkAPI
  ) => {
    try {
      const { data } = await http.get("/v1/schedules", {
        params: {
          schedules: {
            employeeIds,
            date,
            key: nameKey,
          },
          snakeCase: true,
        },
      });

      if (!Array.isArray(data)) {
        throw new Error("Invalid data format: Expected an array");
      }

      return data as Schedule[];
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data || error.message || "Error fetching schedules"
      );
    }
  }
);

export const schedulesSlice = createSlice({
  name: "schedules",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getSchedules.fulfilled, (state, action) => {
      action.payload.forEach((schedule: Schedule) => {
        const { date, employeeId } = schedule;
        if (!state[date]) {
          state[date] = {};
        }
        state[date][employeeId] = schedule;
      });
    });
    builder.addCase(getSchedules.rejected, (state, action) => {
      console.error("Failed to fetch schedules:", action.payload);
    });
  },
});

export const selectSchedules = (state: RootState) => state.schedules;

export default schedulesSlice.reducer;
